import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    fetchStores,
    updateStoreInactiveStatus,
    fetchZones,
    toggleStore,
    toggleStorePromoted,
    fetchStoreCategories, toggleStoreSchedule,
} from "../service/api/orders";

// ── constants ─────────────────────────────────────────────────────────────────
const STORE_INACTIVE_STATUSES = [
    { value: "inactive_onboarding",       label: "Onboarding" },
    { value: "inactive_store_dispatched", label: "Store Dispatched" },
    { value: "inactive_legal_hold",       label: "Legal Hold" },
    { value: "inactive_low_rating",       label: "Low Rating" },
    { value: "inactive_seasonal",         label: "Seasonal Closure" },
];

const TABS = [
    { key: "active",   label: "Active" },
    { key: "inactive", label: "Inactive" },
];

// ── InactiveStatusPopover ─────────────────────────────────────────────────────
const InactiveStatusPopover = ({ store, onClose, onSave }) => {
    const [selected, setSelected] = useState(store.inactive_status ?? null);
    const [saving,   setSaving]   = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [onClose]);

    const handleSave = async () => {
        setSaving(true);
        try { await onSave(store.id, selected); onClose(); }
        finally { setSaving(false); }
    };

    return (
        <div ref={ref} style={{ position:"absolute", zIndex:999, background:"#fff", border:"1px solid #e2e8f0", borderRadius:10, boxShadow:"0 8px 24px rgba(0,0,0,0.12)", padding:12, minWidth:220, right:0, top:"calc(100% + 6px)" }}>
            <div style={{ fontSize:12, fontWeight:600, color:"#64748b", marginBottom:8 }}>Set Inactive Reason</div>
            <label style={optionStyle(selected === null)}>
                <input type="radio" style={{ display:"none" }} checked={selected === null} onChange={() => setSelected(null)} />
                <span style={dotStyle(selected === null, "#10b981")} />
                <span style={{ fontSize:13 }}>Active (clear reason)</span>
            </label>
            {STORE_INACTIVE_STATUSES.map(({ value, label }) => (
                <label key={value} style={optionStyle(selected === value)}>
                    <input type="radio" style={{ display:"none" }} checked={selected === value} onChange={() => setSelected(value)} />
                    <span style={dotStyle(selected === value, "#f59e0b")} />
                    <span style={{ fontSize:13 }}>{label}</span>
                </label>
            ))}
            <button onClick={handleSave} disabled={saving} style={{ marginTop:10, width:"100%", padding:"7px 0", fontSize:13, fontWeight:600, borderRadius:6, border:"none", background:"#6366f1", color:"#fff", cursor:saving?"not-allowed":"pointer", opacity:saving?0.7:1 }}>
                {saving ? "Saving…" : "Apply"}
            </button>
        </div>
    );
};

const optionStyle = (active) => ({ display:"flex", alignItems:"center", gap:8, padding:"6px 8px", borderRadius:6, cursor:"pointer", background:active?"#f1f5f9":"transparent", userSelect:"none" });
const dotStyle   = (active, color) => ({ width:8, height:8, borderRadius:"50%", flexShrink:0, background:active?color:"#cbd5e1", transition:"background 0.15s" });

// ── RestaurantImage ───────────────────────────────────────────────────────────
const RestaurantImage = ({ name, image }) => (
    <img src={"https://food.totalfresh.co/" + image} alt={name} style={{ width:72, height:72, borderRadius:10, objectFit:"cover" }} />
);

// ── Toggle ────────────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, disabled, size = "md" }) {
    const w = size === "sm" ? 34 : 42;
    const h = size === "sm" ? 20 : 24;
    const d = size === "sm" ? 14 : 18;
    const onL = size === "sm" ? 16 : 21;
    return (
        <div
            onClick={disabled ? undefined : onChange}
            style={{ width:w, height:h, borderRadius:h/2, cursor:disabled?"not-allowed":"pointer", background:checked?"#10b981":"#e2e8f0", position:"relative", transition:"background 0.2s", flexShrink:0, opacity:disabled?0.5:1 }}
        >
            <div style={{ width:d, height:d, borderRadius:"50%", background:"#fff", position:"absolute", top:(h-d)/2, left:checked?onL:(h-d)/2, transition:"left 0.2s", boxShadow:"0 1px 4px rgba(0,0,0,0.18)" }} />
        </div>
    );
}

// ── PromotedBadge ─────────────────────────────────────────────────────────────
function PromotedBadge({ on }) {
    return on ? (
        <span style={{ fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:20, background:"#fef3c7", color:"#92400e", border:"1px solid #fde68a", whiteSpace:"nowrap" }}>
            ⭐ Promoted
        </span>
    ) : null;
}

// ── ZoneDropdown ──────────────────────────────────────────────────────────────
function ZoneDropdown({ zones, value, onChange }) {
    return (
        <select
            value={value ?? ""}
            onChange={e => onChange(e.target.value || null)}
            style={{ padding:"9px 32px 9px 14px", border:"1px solid #e2e8f0", borderRadius:20, fontSize:13, fontFamily:"'Inter',sans-serif", color: value ? "#1e293b" : "#94a3b8", background:"#f8fafc url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\") no-repeat right 12px center", outline:"none", cursor:"pointer", fontWeight:500, appearance:"none", WebkitAppearance:"none", minWidth:140 }}
        >
            <option value="">All Zones</option>
            {zones.map(z => (
                <option key={z.id} value={z.id}>{z.name}</option>
            ))}
        </select>
    );
}

// ── CategoryDropdown ──────────────────────────────────────────────────────────
function CategoryDropdown({ categories, value, onChange }) {
    return (
        <select
            value={value ?? ""}
            onChange={e => onChange(e.target.value || null)}
            style={{
                padding:"9px 32px 9px 14px", border:"1px solid #e2e8f0", borderRadius:20,
                fontSize:13, fontFamily:"'Inter',sans-serif",
                color: value ? "#1e293b" : "#94a3b8",
                background:"#f8fafc url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\") no-repeat right 12px center",
                outline:"none", cursor:"pointer", fontWeight:500,
                appearance:"none", WebkitAppearance:"none", minWidth:150
            }}
        >
            <option value="">All Categories</option>
            {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
            ))}
        </select>
    );
}

// ── SkeletonRow ───────────────────────────────────────────────────────────────
function SkeletonRow({ cols = 9 }) {
    return (
        <tr>
            {Array.from({ length: cols }).map((_, i) => (
                <td key={i} style={{ padding:"14px 16px" }}>
                    <div style={{ height:i===0?72:14, width:i===0?72:140, borderRadius:i===0?10:6, background:"#f1f5f9", animation:"pulse 1.4s ease-in-out infinite" }} />
                </td>
            ))}
        </tr>
    );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Stores() {
    const [stores,       setStores]       = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [search,       setSearch]       = useState("");
    const [page,         setPage]         = useState(1);
    const [perPage,      setPerPage]      = useState(10);
    const [meta,         setMeta]         = useState({ total:0, last_page:1, from:0, to:0 });
    const [sortBy,       setSortBy]       = useState({ col:null, dir:1 });
    const [activeTab,    setActiveTab]    = useState("active");
    const [openPopover,  setOpenPopover]  = useState(null);
    const [zones,        setZones]        = useState([]);          // zone list
    const [zoneFilter,   setZoneFilter]   = useState(null);        // selected zone_id
    const [categories,     setCategories]    = useState([]);
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [togglingId,   setTogglingId]   = useState(null);        // active toggle busy
    const [promoId,      setPromoId]      = useState(null);        // promo toggle busy
    const [scheduling, setScheduling]  =  useState(null);
    const debounceRef = useRef(null);
    const navigate = useNavigate();

    const openRestaurantUrl = (url) => {
        if (!url) return;
        const target = String(url);
        if (target.startsWith("/admin") || target.startsWith("http")) {
            window.location.href = target;
            return;
        }
        navigate(target);
    };

    // Load zones once on mount
    useEffect(() => {
        fetchZones()
            .then(data => setZones(Array.isArray(data) ? data : (data.data ?? [])))
            .catch(console.error);
    }, []);

    // Load categories once on mount
    useEffect(() => {
        fetchStoreCategories()
            .then(data => setCategories(Array.isArray(data) ? data : (data.data ?? [])))
            .catch(console.error);
    }, []);

    const load = useCallback(async (q, pg, pp, tab, zoneId, categoryId) => {
        setLoading(true);
        try {
            const params = { q, page: pg, per_page: pp };
            if (tab === "inactive") params.with_inactive = true;
            if (zoneId)             params.zone_id        = zoneId;
            if (categoryId)         params.category_id    = categoryId;
            const res = await fetchStores(params);
            setStores(res.data ?? []);
            setMeta({ total:res.total??0, last_page:res.last_page??1, from:res.from??0, to:res.to??0 });
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(1);
            load(search, 1, perPage, activeTab, zoneFilter, categoryFilter);
        }, 350);
        return () => clearTimeout(debounceRef.current);
    }, [search, perPage, activeTab, zoneFilter, categoryFilter]);

    useEffect(() => {
        load(search, page, perPage, activeTab, zoneFilter, categoryFilter);
    }, [page]);

    // ── Active toggle (with API call) ────────────────────────────────────────
    const handleToggleActive = async (store) => {
        if (togglingId) return;
        setTogglingId(store.id);
        // Optimistic
        setStores(s => s.map(r => r.id === store.id ? { ...r, is_active: r.is_active ? 0 : 1 } : r));
        try {
            await toggleStore(store?.id);
        } catch (e) {
            // Revert on failure
            setStores(s => s.map(r => r.id === store.id ? { ...r, is_active: store.is_active } : r));
            console.error(e);
        } finally {
            setTogglingId(null);
        }
    };

    // ── Promoted toggle ──────────────────────────────────────────────────────
    const handleTogglePromo = async (store) => {
        if (promoId) return;
        setPromoId(store.id);
        // Optimistic — adjust field name to whatever your model uses
        setStores(s => s.map(r => r.id === store.id ? { ...r, is_promoted: r.is_promoted ? 0 : 1 } : r));
        try {
            await toggleStorePromoted(store?.id);
        } catch (e) {
            setStores(s => s.map(r => r.id === store.id ? { ...r, is_promoted: store.is_promoted } : r));
            console.error(e);
        } finally {
            setPromoId(null);
        }
    };


    const handleToggleSchedule = async (store) => {
        if (scheduling) return;
        setScheduling(store.id);
        // Optimistic — adjust field name to whatever your model uses
        setStores(s => s.map(r => r.id === store.id ? { ...r, scheduling_enabled: !r.scheduling_enabled} : r));
        try {
            await toggleStoreSchedule(store?.id);
        } catch (e) {
            setStores(s => s.map(r => r.id === store.id ? { ...r, scheduling_enabled: store.scheduling_enabled } : r));
            console.error(e);
        } finally {
            setScheduling(null);
        }
    };


    // ── Inactive status ──────────────────────────────────────────────────────
    const handleSaveInactiveStatus = async (id, inactiveStatus) => {
        try {
            await updateStoreInactiveStatus(id, inactiveStatus);
            if (activeTab === "inactive" && inactiveStatus === null) {
                setStores(s => s.filter(r => r.id !== id));
            } else {
                setStores(s => s.map(r => r.id === id ? { ...r, inactive_status: inactiveStatus } : r));
            }
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.message || "Something went wrong");
        }
    };

    const exportCSV = () => {
        const header = ["Name", "Zone", "Areas", "Owners", "Joined", "Active", "Promoted", "Inactive Reason"];
        const rows = stores.map(r => [
            r.name, r.zone?.name ?? "",
            (r.delivery_areas ?? []).map(a => a.name).join("; "),
            (r.users ?? []).map(o => o.name).join("; "),
            r.created_at, r.is_active ? "Yes" : "No",
            r.is_promoted ? "Yes" : "No",
            r.inactive_status ?? "",
        ]);
        const csv = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type:"text/csv" });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement("a"); a.href = url; a.download = "stores.csv"; a.click();
        URL.revokeObjectURL(url);
    };

    const buildPages = () => {
        const pages = []; const total = meta.last_page;
        if (total <= 7) { for (let i = 1; i <= total; i++) pages.push(i); return pages; }
        pages.push(1);
        if (page > 3) pages.push("…");
        for (let i = Math.max(2, page-1); i <= Math.min(total-1, page+1); i++) pages.push(i);
        if (page < total-2) pages.push("…");
        pages.push(total);
        return pages;
    };

    const SortIcon = ({ col }) => {
        const active = sortBy.col === col;
        return (
            <span style={{ marginLeft:4, opacity:active?1:0.35, fontSize:11, display:"inline-flex", flexDirection:"column", lineHeight:1, gap:1 }}>
                <span style={{ color:active&&sortBy.dir===1?"#6366f1":"#94a3b8" }}>▲</span>
                <span style={{ color:active&&sortBy.dir===-1?"#6366f1":"#94a3b8" }}>▼</span>
            </span>
        );
    };

    const handleSort = (col) => {
        setSortBy(prev => ({ col, dir: prev.col === col ? -prev.dir : 1 }));
        setStores(s => [...s].sort((a, b) => {
            const va = a[col]??""; const vb = b[col]??"";
            return String(va).localeCompare(String(vb)) * (sortBy.col === col ? -sortBy.dir : 1);
        }));
    };

    const isInactiveTab = activeTab === "inactive";

    return (
        <div style={{ margin:25, fontFamily:"'Inter', sans-serif" }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.45} }
                .str-row:hover { background: #fafbff !important; }
                .str-owner-link { color:#f97316; font-weight:600; font-size:13px; display:inline-flex; align-items:center; gap:5px; cursor:pointer; text-decoration:none; transition:opacity 0.15s; }
                .str-owner-link:hover { opacity:0.75; }
                .str-edit-btn { display:inline-flex; align-items:center; gap:5px; padding:6px 14px; font-size:13px; font-weight:600; border-radius:8px; border:none; background:#528ff0; color:#fff; cursor:pointer; font-family:'Inter',sans-serif; white-space:nowrap; transition:all 0.2s ease; }
                .str-edit-btn:hover { opacity:0.8; transform:translateY(-1px); }
                .str-page-btn { width:34px; height:34px; border-radius:8px; font-size:13px; font-family:'Inter',sans-serif; font-weight:600; border:1px solid #e2e8f0; background:#f8fafc; color:#475569; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all 0.15s; flex-shrink:0; }
                .str-page-btn:hover:not(:disabled):not(.str-page-active) { background:#f1f5f9; border-color:#cbd5e1; }
                .str-page-active { border-color:#6366f1 !important; background:#eef2ff !important; color:#6366f1 !important; border-width:1.5px !important; }
                .str-page-btn:disabled { opacity:0.35; cursor:not-allowed; }
                .str-th-sort { cursor:pointer; user-select:none; }
                .str-th-sort:hover { color:#1e293b; }
                .str-tab { padding:8px 18px; font-size:13px; font-weight:600; border:none; border-bottom:2px solid transparent; background:none; cursor:pointer; color:#94a3b8; font-family:'Inter',sans-serif; transition:all 0.15s; }
                .str-tab:hover { color:#475569; }
                .str-tab.active { color:#6366f1; border-bottom-color:#6366f1; }
                .rst-action-cell { display:inline-flex; align-items:center; justify-content:center; gap:6px; flex-wrap:nowrap; min-width:108px; }
                .rst-edit-btn, .rst-items-btn { height:32px; min-width:46px; border-radius:7px; padding:0 11px; border:1px solid transparent; font-size:12px; font-weight:800; font-family:'Inter',sans-serif; cursor:pointer; white-space:nowrap; transition:background 0.15s ease, border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease; }
                .rst-edit-btn { background:#3b82f6; border-color:#3b82f6; color:#fff; }
                .rst-edit-btn:hover { background:#2563eb; border-color:#2563eb; transform:translateY(-1px); box-shadow:0 4px 10px rgba(37,99,235,0.22); }
                .rst-items-btn { background:#f97316; border-color:#f97316; color:#fff; }
                .rst-items-btn:hover { background:#ea580c; border-color:#ea580c; transform:translateY(-1px); box-shadow:0 4px 10px rgba(249,115,22,0.24); }
                @media (max-width: 768px) { .rst-action-cell { justify-content:flex-start; min-width:116px; } .rst-edit-btn, .rst-items-btn { min-width:44px; padding:0 10px; } }
            `}</style>

            {/* Header */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
                <div>
                    <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:"#1e293b" }}>Stores</h1>
                    <p style={{ margin:"4px 0 0", fontSize:13, color:"#94a3b8" }}>Manage all restaurant stores</p>
                </div>
                <button className="str-edit-btn" onClick={exportCSV} style={{ background:"#fff", color:"#475569", border:"1px solid #e2e8f0", boxShadow:"0 1px 3px rgba(0,0,0,0.06)" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Export as CSV
                </button>
            </div>

            <div style={{ background:"#fff", borderRadius:14, border:"1px solid #edf2f7", boxShadow:"0 2px 12px rgba(0,0,0,0.05)", overflow:"hidden" }}>

                {/* Tabs */}
                <div style={{ display:"flex", borderBottom:"1px solid #edf2f7", paddingLeft:8 }}>
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            className={`str-tab${activeTab === tab.key ? " active" : ""}`}
                            onClick={() => { setActiveTab(tab.key); setPage(1); setSearch(""); setZoneFilter(null); setCategoryFilter(null); }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Toolbar */}
                <div style={{ display:"flex", alignItems:"center", gap:10, padding:"14px 20px", borderBottom:"1px solid #edf2f7", flexWrap:"wrap", background:"#fff" }}>
                    {/* Search */}
                    <div style={{ position:"relative", flex:"1 1 200px", minWidth:0 }}>
                        <span style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)", color:"#94a3b8", pointerEvents:"none", display:"flex", alignItems:"center" }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                            </svg>
                        </span>
                        <input
                            style={{ width:"100%", padding:"9px 12px 9px 34px", border:"1px solid #e2e8f0", borderRadius:20, fontSize:13.5, fontFamily:"'Inter',sans-serif", color:"#1e293b", background:"#f8fafc", outline:"none", boxSizing:"border-box" }}
                            placeholder="Search with anything..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>

                    <div style={{ display:"flex", alignItems:"center", gap:8, marginLeft:"auto", flexWrap:"wrap" }}>
                        {/* Zone filter */}
                        <ZoneDropdown
                            zones={zones}
                            value={zoneFilter}
                            onChange={v => { setZoneFilter(v); setPage(1); }}
                        />

                        {/* Category filter */}
                        <CategoryDropdown
                            categories={categories}
                            value={categoryFilter}
                            onChange={v => { setCategoryFilter(v); setPage(1); }}
                        />

                        <select
                            style={{ padding:"9px 32px 9px 14px", border:"1px solid #e2e8f0", borderRadius:20, fontSize:13, fontFamily:"'Inter',sans-serif", color:"#475569", background:"#f8fafc url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\") no-repeat right 12px center", outline:"none", cursor:"pointer", fontWeight:500, appearance:"none", WebkitAppearance:"none" }}
                            value={perPage}
                            onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                        >
                            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>

                        <span style={{ fontSize:13, color:"#94a3b8", fontWeight:500, whiteSpace:"nowrap" }}>
                            {loading ? "Loading…" : `${(meta.total ?? 0).toLocaleString()} entries`}
                        </span>
                    </div>
                </div>

                {/* Table */}
                <div style={{ overflowX:"auto", WebkitOverflowScrolling:"touch" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse", minWidth:820 }}>
                        <thead>
                        <tr style={{ background:"#f8fafc", borderBottom:"1px solid #edf2f7" }}>
                            {[
                                { label:"Image",             col:null },
                                { label:"Name",              col:"name" },
                                { label:"Operational Areas", col:null },
                                { label:"Owner",             col:null },
                                { label:"Joined Date",       col:"created_at" },
                                isInactiveTab ? { label:"Inactive Reason", col:null } : { label:"", col:null },
                                { label:"Promoted",          col:null },
                                { label:"Scedled",          col:null },
                                { label:"Active",            col:null },
                            ].map(({ label, col }, i) => (
                                <th
                                    key={i}
                                    className={col ? "str-th-sort" : ""}
                                    onClick={col ? () => handleSort(col) : undefined}
                                    style={{ padding:"12px 16px", textAlign:"left", fontSize:13, fontWeight:700, color:"#555", textTransform:"capitalize", letterSpacing:"0.07em", fontFamily:"'Inter',sans-serif", whiteSpace:"nowrap" }}
                                >
                                    {label}{col && <SortIcon col={col} />}
                                </th>
                            ))}
                            <th style={{ padding:"12px 16px", textAlign:"center", fontSize:13, fontWeight:800, color:"#111827", whiteSpace:"nowrap", width:128 }}>Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        {loading
                            ? Array.from({ length: Math.min(perPage, 5) }).map((_, i) => <SkeletonRow key={i} cols={9} />)
                            : stores.length === 0
                                ? (
                                    <tr><td colSpan={9}>
                                        <div style={{ padding:"56px 20px", textAlign:"center" }}>
                                            <div style={{ fontSize:36, marginBottom:10 }}>🏪</div>
                                            <div style={{ fontSize:15, fontWeight:600, color:"#1e293b", marginBottom:4 }}>
                                                {isInactiveTab ? "No inactive stores" : "No stores found"}
                                            </div>
                                            <div style={{ fontSize:13, color:"#94a3b8" }}>
                                                {isInactiveTab ? "All stores are currently active" : "Try adjusting your search or filters"}
                                            </div>
                                        </div>
                                    </td></tr>
                                )
                                : stores.map(store => {
                                    const inactiveLabel = STORE_INACTIVE_STATUSES.find(s => s.value === store.inactive_status)?.label ?? null;
                                    const isTogglingThis = togglingId === store.id;
                                    const isPromoThis    = promoId    === store.id;

                                    return (
                                        <tr key={store.id} className="str-row"
                                            style={{borderBottom: "1px solid #f1f5f9", transition: "background 0.12s"}}>

                                            {/* Image + promoted badge stacked */}
                                            <td style={{padding: "14px 16px", verticalAlign: "middle"}}>
                                                <div style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "flex-start",
                                                    gap: 6
                                                }}>
                                                    <RestaurantImage name={store.name} image={store.image}/>
                                                    <PromotedBadge on={!!store.is_promoted}/>
                                                </div>
                                            </td>

                                            {/* Name */}
                                            <td style={{padding: "14px 16px", verticalAlign: "middle"}}>
                                                <span style={{
                                                    fontSize: 14,
                                                    fontWeight: 600,
                                                    color: "#1e293b",
                                                    maxWidth: 220,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    display: "block"
                                                }}>
                                                    {store.name}
                                                </span>
                                            </td>

                                            {/* Areas + Zone */}
                                            <td style={{padding: "14px 16px", verticalAlign: "middle"}}>
                                                <div style={{display: "flex", flexDirection: "column", gap: 3}}>
                                                    <span style={{
                                                        fontSize: 13,
                                                        color: "#64748b"
                                                    }}>Areas: {(store.delivery_areas ?? []).map(a => a.name).join(", ") || "—"}</span>
                                                    <span style={{
                                                        fontSize: 13,
                                                        color: "#94a3b8"
                                                    }}>Zone: {store.zone?.name ?? "—"}</span>
                                                </div>
                                            </td>

                                            {/* Owners */}
                                            <td style={{padding: "14px 16px", verticalAlign: "middle"}}>
                                                <div style={{display: "flex", flexDirection: "column", gap: 5}}>
                                                    {(store.users ?? []).map(o => (
                                                        <a key={o.id} className="str-owner-link" href="#"
                                                           onClick={e => e.preventDefault()}>
                                                            {o.name}
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                                                                 stroke="currentColor" strokeWidth="2.2"
                                                                 strokeLinecap="round" strokeLinejoin="round">
                                                                <circle cx="12" cy="12" r="10"/>
                                                                <polyline points="12 8 16 12 12 16"/>
                                                                <line x1="8" y1="12" x2="16" y2="12"/>
                                                            </svg>
                                                        </a>
                                                    ))}
                                                </div>
                                            </td>

                                            {/* Date */}
                                            <td style={{padding: "14px 16px", verticalAlign: "middle"}}>
                                                <span style={{
                                                    fontSize: 13,
                                                    color: "#64748b",
                                                    whiteSpace: "nowrap"
                                                }}>{store.created_at}</span>
                                            </td>

                                            {/* Inactive reason / popover */}
                                            <td style={{padding: "14px 16px", verticalAlign: "middle"}}>
                                                <div style={{
                                                    position: "relative",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 6
                                                }}>
                                                    {inactiveLabel && (
                                                        <span style={{
                                                            fontSize: 12,
                                                            fontWeight: 500,
                                                            padding: "2px 8px",
                                                            borderRadius: 20,
                                                            whiteSpace: "nowrap",
                                                            background: "#fef3c7",
                                                            color: "#92400e",
                                                            border: "1px solid #fde68a"
                                                        }}>
                                                            {inactiveLabel}
                                                        </span>
                                                    )}
                                                    <button
                                                        type="button"
                                                        title="Set inactive reason"
                                                        onClick={() => setOpenPopover(openPopover === store.id ? null : store.id)}
                                                        style={{
                                                            background: "none",
                                                            border: "0.5px solid #e2e8f0",
                                                            borderRadius: 6,
                                                            padding: "3px 6px",
                                                            cursor: "pointer",
                                                            color: "#64748b",
                                                            display: "flex",
                                                            alignItems: "center"
                                                        }}
                                                    >
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                                             stroke="currentColor" strokeWidth="2.5"
                                                             strokeLinecap="round" strokeLinejoin="round">
                                                            <path
                                                                d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                                            <path
                                                                d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                        </svg>
                                                    </button>
                                                    {openPopover === store.id && (
                                                        <InactiveStatusPopover
                                                            store={store}
                                                            onClose={() => setOpenPopover(null)}
                                                            onSave={handleSaveInactiveStatus}
                                                        />
                                                    )}
                                                </div>
                                            </td>

                                            {/* Promoted toggle */}
                                            <td style={{padding: "14px 16px", verticalAlign: "middle"}}>
                                                <div style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "flex-start",
                                                    gap: 4
                                                }}>
                                                    <Toggle
                                                        checked={!!store.is_promoted}
                                                        onChange={() => handleTogglePromo(store)}
                                                        disabled={isPromoThis}
                                                        size="m"
                                                    />
                                                    <span style={{
                                                        fontSize: 11,
                                                        color: store.is_promoted ? "#f59e0b" : "#94a3b8",
                                                        fontWeight: 600
                                                    }}>
                                                        {isPromoThis ? "…" : store.is_promoted ? "Promoted" : "Normal"}
                                                    </span>
                                                </div>
                                            </td>

                                            <td style={{padding: "14px 16px", verticalAlign: "middle"}}>
                                                <div style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "flex-start",
                                                    gap: 4
                                                }}>
                                                    <Toggle
                                                        checked={!!store.scheduling_enabled}
                                                        onChange={() => handleToggleSchedule(store)}
                                                        disabled={scheduling}
                                                        size="m"
                                                    />

                                                    <span style={{
                                                        fontSize: 11,
                                                        color: store.scheduling_enabled ? "#10b981" : "#94a3b8",
                                                        fontWeight: 600
                                                    }}>
                                                      Schedule
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Active toggle */}
                                            <td style={{padding: "14px 16px", verticalAlign: "middle"}}>
                                                {!isInactiveTab && (
                                                    <div style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "flex-start",
                                                        gap: 4
                                                    }}>
                                                        <Toggle
                                                            checked={!!store.is_active}
                                                            onChange={() => handleToggleActive(store)}
                                                            disabled={isTogglingThis}
                                                        />
                                                        <span style={{
                                                            fontSize: 11,
                                                            color: store.is_active ? "#10b981" : "#94a3b8",
                                                            fontWeight: 600
                                                        }}>
                                                            {isTogglingThis ? "…" : store.is_active ? "Active" : "Inactive"}
                                                        </span>
                                                    </div>
                                                )}
                                            </td>

                                            {/* Actions: Edit / Items */}
                                            <td style={{padding: "14px 16px", verticalAlign: "middle"}}>
                                                <div className="rst-action-cell">
                                                    <button
                                                        type="button"
                                                        className="rst-edit-btn"
                                                        onClick={() => navigate(`/stores/edit/${store.id}/sort`)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="rst-items-btn"
                                                        onClick={() => navigate(`/stores/${store.id}/items`)}
                                                    >
                                                        Items
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                        }
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!loading && stores.length > 0 && (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "13px 20px",
                        borderTop: "1px solid #edf2f7",
                        flexWrap: "wrap",
                        gap: 8,
                        background: "#fff"
                    }}>
                        <span style={{fontSize: 13, color: "#94a3b8", fontWeight: 500}}>
                            Page {page} of {meta.last_page} · {meta.total.toLocaleString()} entries
                        </span>
                        <div style={{display: "flex", alignItems: "center", gap: 4}}>
                            <button className="str-page-btn" disabled={page === 1}
                                    onClick={() => setPage(p => p - 1)}>‹
                            </button>
                            {buildPages().map((p, i) =>
                                p === "…"
                                    ? <span key={`e${i}`} style={{color:"#cbd5e1", fontSize:13, padding:"0 2px" }}>…</span>
                                    : <button key={p} className={`str-page-btn${page===p?" str-page-active":""}`} onClick={() => setPage(p)}>{p}</button>
                            )}
                            <button className="str-page-btn" disabled={page===meta.last_page} onClick={() => setPage(p => p+1)}>›</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}